import { Home, Building, KeyRound, Droplets, Wallet } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { fmt } from "./oversiktConstants";
import { dec } from "@/lib/formatters";

const Tile = ({ icon: Icon, label, value, sub, href, testId, estimate }) => (
  <a
    href={href}
    className="group p-4 rounded-lg border border-[#E6E9F0] bg-white hover:border-[#103766] hover:shadow-sm transition-colors block"
    data-testid={testId}
  >
    <div className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] flex items-center gap-2">
      <Icon className="w-3.5 h-3.5 text-[#103766]" /> {label}
      {estimate && <Badge variant="outline" className="border-[#E8A34A] text-[#B8741F] bg-[#FDF6EA] text-[9px] uppercase">Estimat</Badge>}
    </div>
    <p className="text-2xl font-light text-[#1F2A36] mt-1">{value ?? "—"}</p>
    {sub && <p className="text-xs text-[#4B647F] mt-1">{sub}</p>}
  </a>
);

// Summary strip that links all housing-cost modules together
const BoligOverview = ({ bokostnad, boligpriser, leie, gebyrer, medianHouseholdIncome, incomeLabel = "Median samla inntekt per hushald (før skatt)" }) => {
  const bk = bokostnad?.kommune;
  const bp = (boligpriser?.kommune || []).slice(-1)[0];
  const rent = (leie?.average?.series || []).slice(-1)[0];
  const geb = (gebyrer || []).slice(-1)[0];
  const rentShare = rent && medianHouseholdIncome ? ((rent.monthly * 12) / medianHouseholdIncome) * 100 : null;
  const ownShare = bk && medianHouseholdIncome ? (bk.total / medianHouseholdIncome) * 100 : null;

  return (
    <section className="mb-8" data-testid="bolig-overview">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="text-base md:text-lg font-medium text-[#1F2A36]">Bustad og bokostnader</h2>
          <p className="text-sm text-[#4B647F]">Kva kostar det å eige og leige bustad i kommunen? Klikk på eit tal for å gå til modulen.</p>
        </div>
        {(ownShare != null || rentShare != null) && (
          <p className="text-xs text-[#4B647F] bg-[#F6F8FB] rounded-lg px-3 py-2" data-testid="bolig-overview-shares">
            Andel av median hushaldsinntekt:
            {ownShare != null && <> eige <span className="font-medium text-[#1F2A36]">{dec(ownShare, 1)} %</span></>}
            {ownShare != null && rentShare != null && " · "}
            {rentShare != null && <> leige 2-roms <span className="font-medium text-[#1F2A36]">{dec(rentShare, 1)} %</span></>}
          </p>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Tile icon={Home} label={`Bokostnad ${bk?.year ?? ""}`} value={bk ? `${fmt(bk.total)} kr/år` : null} sub="Einebustad 120 m² (Huseierne)" href="#bokostnad" testId="bolig-tile-bokostnad" />
        <Tile icon={Building} label={`Kvadratmeterpris ${bp ? `${bp.year} K${bp.quarter}` : ""}`} value={bp?.alle?.kvm_pris ? `${fmt(bp.alle.kvm_pris)} kr/m²` : null} sub={bp ? `Alle bustadtypar · ${fmt(bp.alle?.omsetninger)} omsetningar` : "Alle bustadtypar"} href="#boligpriser" testId="bolig-tile-boligpriser" />
        <Tile icon={KeyRound} label={`Leige 2-roms ${rent?.year ?? ""}`} value={rent ? `${fmt(rent.monthly)} kr/mnd` : null} sub={leie?.average?.zone_label} href="#leie" testId="bolig-tile-leie" estimate={leie?.average?.estimate} />
        <Tile icon={Droplets} label={`Kommunale gebyr ${geb?.year ?? ""}`} value={geb ? `${fmt(geb.sum_arsgebyr)} kr/år` : null} sub="Vatn, avløp, avfall og feiing" href="#gebyrer" testId="bolig-tile-gebyrer" />
      </div>
      {medianHouseholdIncome && (
        <p className="text-xs text-[#8AA4BF] mt-2 flex items-center gap-1"><Wallet className="w-3 h-3" /> {incomeLabel}: {fmt(medianHouseholdIncome)} kr</p>
      )}
    </section>
  );
};

export default BoligOverview;
